import consolv from 'lib/consolv';

const renderKeys = [
  'isEmpty',
  'serviceName',
  'slotContentChanged',
  'size',
  'advertiserId',
  'campaignId',
  'creativeId',
  'labelIds',
  'lineItemId',
  'sourceAgnosticCreativeId',
  'sourceAgnosticLineItemId',
  'isBackfill',
  'yieldGroupIds',
  'companyIds',
];

const slotFunctions = [
  'getAdUnitPath',
  'getTargeting',
  'setTargeting',
  'getTargetingMap',
  'clearTargeting',
];

/**
 * A custom wrapper for the out-of-page DFP ad slot that exposes additional information and
 * functionality.
 */
export default class DFPOutOfPageAdSlot {
  _MCP;

  _DFPSlot;

  _type;

  _id;

  _creative = {
    isEmpty: true,
  };

  _meta = {
    stale: false,
    loaded: false,
  };

  _clearTargetingAfterRefresh = [];

  /**
   * @param {Object} params
   * @param {MCPInstance} params.MCP
   * @param {SlotInstance} params.slot Slot instance provided by GPT library event after
   * slot has been created
   * @param {SlotType} params.type The type of target ad slot
   * @param {MetaList} params.meta Initial meta to add to target ad slot
   */
  constructor({ MCP, slot: googleTagSlot, type, meta }) {
    this.handleSlotRenderEnded = this.handleSlotRenderEnded.bind(this);
    this.toPlainObject = this.toPlainObject.bind(this);
    this.clearTargetingAfterRefresh = this.clearTargetingAfterRefresh.bind(this);
    this.refresh = this.refresh.bind(this);
    this.resetSlot = this.resetSlot.bind(this);
    this.destroy = this.destroy.bind(this);
    this.getMetaData = this.getMetaData.bind(this);
    this.setMetaData = this.setMetaData.bind(this);
    this.removeMetaData = this.removeMetaData.bind(this);

    if (typeof googleTagSlot !== 'object') {
      throw new Error('This must be constructed with a DFP Slot object');
    }

    this._MCP = MCP;
    this._DFPSlot = googleTagSlot;
    this._id = this._DFPSlot.getSlotElementId();
    this._type = type;
    this._meta = { ...meta, ...this._meta };

    slotFunctions.forEach((property) => {
      this[property] = googleTagSlot[property].bind(googleTagSlot);
    });
  }

  /**
   * @private
   */
  handleSlotRenderEnded(e) {
    if (e.slot !== this._DFPSlot) return;

    renderKeys.forEach((key) => {
      this._creative[key] = e[key];
    });
    this.setMetaData('creativeLoaded', true);
    this.setMetaData('renderedAt', new Date());
    this.setMetaData('loaded', true);
    this.setMetaData('stale', false);

    this._clearTargetingAfterRefresh.forEach((key) => { this.clearTargeting(key); });
    this._clearTargetingAfterRefresh = [];
  }

  toPlainObject() {
    return {
      meta: this._meta,
      creative: this.creative,
      id: this.id,
      type: this.type,
      targeting: this.targeting,
    };
  }

  get adUnitPath() {
    return this.getAdUnitPath();
  }

  get DFPSlot() {
    return this._DFPSlot;
  }

  get creative() {
    return this._creative;
  }

  get meta() {
    return this._meta;
  }

  get id() {
    return this._id;
  }

  get type() {
    return this._type;
  }

  get targeting() {
    return this._DFPSlot.getTargetingMap();
  }

  setMetaData(key, value) {
    this._meta[key] = value;
  }

  getMetaData(key) {
    return this._meta[key];
  }

  removeMetaData(key) {
    delete this._meta[key];
  }

  clearTargetingAfterRefresh(arrayOfKeys) {
    if (!Array.isArray(arrayOfKeys)) return;
    this._clearTargetingAfterRefresh.push(...arrayOfKeys);
  }

  resetSlot() {
    this.removeMetaData('creativeLoaded');
    this._creative = {};
  }

  refresh(additionalTargeting = {}) {
    const { googletag } = window;
    const DFPSlot = this._DFPSlot;
    const oldTargeting = DFPSlot.getTargetingMap();

    Object.keys(additionalTargeting).forEach((key) => {
      DFPSlot.setTargeting(key, additionalTargeting[key]);
    });

    consolv.log('DFP: Direct Slot Refresh', this);
    googletag.pubads().refresh([DFPSlot]);

    Object.keys(additionalTargeting).forEach((key) => {
      if (oldTargeting[key]) {
        DFPSlot.setTargeting(key, additionalTargeting[key]);
      } else {
        DFPSlot.clearTargeting(key);
      }
    });

    this.resetSlot();
  }

  destroy() {
    const { googletag } = window;
    googletag.destroySlots([this._DFPSlot]);
  }
}
